// Dimensioned reusable objects. Detailed display meshes have the same footprint
// and height as these conservative analytical obstructions.
export const SCENE_PROPS = {
  sofa: {label:'Sofa',width:2.4,depth:.9,height:.9,asset:'sofa_03'},
  armchair: {label:'Armchair',width:.85,depth:.9,height:1,asset:'modern_arm_chair_01'},
  bench: {label:'Garden bench',width:1.8,depth:.6,height:.9,asset:'painted_wooden_bench'},
  plant: {label:'Potted plant',width:.6,depth:.6,height:1.35,asset:'potted_plant_01'},
  tree: {label:'Deciduous tree',width:3.5,depth:4,height:5,asset:'tree_small_02'},
  hedge: {label:'Hedge',width:3,depth:.7,height:1.2},
  bed: {label:'Double bed',width:2.05,depth:1.6,height:.95},
  cabinet: {label:'Storage cabinet',width:1.2,depth:.55,height:1.9},
  rack: {label:'Pallet rack',width:2.7,depth:1.1,height:5.5},
  gondola: {label:'Stocked retail shelving',width:3.2,depth:1,height:1.7},
  bookcase: {label:'Bookcase with books',width:2,depth:.4,height:1.8},
  'dining-table': {label:'Dining / meeting table',width:2,depth:1,height:.75},
  monitor: {label:'Desk monitor',width:.15,depth:.6,height:.42},
  whiteboard: {label:'Whiteboard',width:.06,depth:2,height:1.2},
  toilet: {label:'Toilet',width:.72,depth:.42,height:.8},
  basin: {label:'Washbasin / vanity',width:.6,depth:.5,height:.85},
  fridge: {label:'Refrigerator',width:.7,depth:.75,height:1.85},
  bollard: {label:'Lighting bollard',width:.16,depth:.16,height:.85}
};
export const propDefaults = kind => SCENE_PROPS[kind] ? Object.fromEntries(Object.entries(SCENE_PROPS[kind]).filter(([k])=>['width','depth','height'].includes(k))) : {};
export const PERSON_APPEARANCES = { reference:'Reference adult', 'adult-female':'Adult · smart casual', 'adult-casual':'Adult · casual clothing' };
export const personAssetKey = target => target.appearance && target.appearance !== 'reference' ? 'person-'+target.appearance : 'person';

export function scenePropParts(o) {
  if(!SCENE_PROPS[o.kind])return null;
  const {width:w,depth:d,height:h}=o,parts=[];
  const box=(role,x,y,z,width,depth,height,color)=>parts.push({role,x,y,z,width,depth,height,color});
  if(['sofa','armchair','bench'].includes(o.kind)){
    const seat=Math.min(.45,h*.5);box('seat',0,0,seat-.07,w,d,.14,'#758080');box('back',0,-d*.42,(seat+h)/2,w,d*.16,h-seat,'#677575');
    for(const side of [-1,1]){box('arm',side*w*.46,0,seat+.12,w*.08,d,.24,'#758080');for(const y of [-1,1])box('leg',side*w*.4,y*d*.35,(seat-.14)/2,.06,.06,seat-.14,'#6f6558');}
  }else if(o.kind==='bed'){
    box('frame',0,0,.18,w,d,.3,'#927c60');box('mattress',0,0,.4,w*.97,d*.96,.22,'#e5ded2');box('headboard',-w*.47,0,h/2,w*.06,d,h,'#8f7e69');
    for(const side of [-1,1])box('pillow',-w*.28,side*d*.23,.55,w*.22,d*.4,.15,'#f1efdf');
  }else if(o.kind==='rack'){
    for(const x of [-1,1])for(const y of [-1,1])box('upright',x*(w-.09)/2,y*(d-.09)/2,h/2,.09,.09,h,'#526673');
    for(let z=.22;z<h;z+=1.45){box('shelf',0,0,z,w,d,.09,'#c27d38');if(z+1<h)box('stock',0,0,z+.48,w*.85,d*.85,.85,'#ae9977');}
  }else if(['gondola','bookcase'].includes(o.kind)){
    const retail=o.kind==='gondola',tiers=retail?4:5,t=.035;
    box('back',0,retail?0:-d*.47,h/2,w,t,h,'#b6b9b3');
    for(const side of [-1,1])box('upright',side*(w-t)/2,0,h/2,t,d,h,'#8e9697');
    const colors=retail?['#798b6f','#b98b67','#d3c69c','#708c9a']:['#967360','#6e8190','#899273','#b8a985'];
    for(let row=0;row<tiers;row++){
      const z=.1+row*(h-.2)/tiers,stockH=(h-.2)/tiers*.7;
      box('shelf',0,0,z,w,d,t,'#bcc0b8');
      for(const side of retail?[-1,1]:[0])for(let col=0;col<4;col++)box(retail?'stock':'books',-w/2+(col+.5)*w/4,retail?side*d*.28:.02,z+t/2+stockH/2,w*.23,d*(retail?.36:.8),stockH,colors[(row+col)%colors.length]);
    }
  }else if(o.kind==='dining-table'){
    box('top',0,0,h-.035,w,d,.07,'#b29670');for(const x of [-1,1])for(const y of [-1,1])box('leg',x*w*.4,y*d*.35,(h-.07)/2,.07,.07,h-.07,'#657078');
  }else if(o.kind==='tree'){
    box('trunk',0,0,h*.3,w*.09,d*.09,h*.6,'#75664d');box('canopy',0,0,h*.7,w,d,h*.6,'#53764c');
  }else if(o.kind==='plant'){
    box('pot',0,0,h*.13,w*.6,d*.6,h*.26,'#9c755a');box('foliage',0,0,h*.58,w,d,h*.84,'#527a43');
  }else if(o.kind==='monitor'){
    box('screen',0,0,h*.64,w*.25,d,h*.7,'#303940');box('stand',0,0,h*.17,w*.3,d*.1,h*.34,'#626b73');box('base',0,0,.015,w,d*.45,.03,'#626b73');
  }else if(o.kind==='whiteboard'){
    box('frame',0,0,h/2,w,d,h,'#7e8990');box('board',w*.53,0,h/2,.01,d*.96,h*.94,'#f0f0e9');
  }else if(o.kind==='toilet'){
    box('tank',-w*.34,0,h*.63,w*.25,d,h*.74,'#eeeae1');box('bowl',w*.13,0,h*.38,w*.69,d*.95,h*.3,'#efeee7');box('base',0,0,h*.19,w*.45,d*.6,h*.38,'#e9e7df');
  }else if(o.kind==='basin'){
    box('vanity',0,0,h*.46,w,d,h*.9,'#a99c86');box('sink',0,0,h*.96,w,d,h*.08,'#f1eee6');
  }else if(o.kind==='fridge'){
    box('body',0,0,h/2,w,d,h,'#c4c9c9');box('door seam',w*.502,0,h*.7,.015,d,.015,'#56616b');box('handle',w*.54,-d*.34,h*.5,.05,.025,h*.22,'#56616b');
  }else if(o.kind==='cabinet'){
    box('body',0,0,h/2,w,d,h,'#b1a58c');for(const side of [-1,1])box('handle',w*.51,side*d*.05,h*.54,.025,.035,.2,'#5f6870');
  }else box('body',0,0,h/2,w,d,h,o.kind==='hedge'?'#52724a':'#52616b');
  return parts;
}
